/**
 * Case Scope API service
 *
 * Provides functions for managing case scope documents, versions, and notes.
 * The scope is a living document workspace for IR analysts.
 */

import { apiClient } from './api'
import type {
  CaseScope,
  CaseScopeVersion,
  CaseScopeVersionList,
  CaseScopeNote,
  CaseScopeNoteList,
  UpdateScopeRequest,
  CreateScopeNoteRequest,
  UpdateScopeNoteRequest,
  ListVersionsQuery,
  ListNotesQuery,
} from '../types/case-container'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// SCOPE DOCUMENT API
// =============================================================================

/**
 * Get scope document for a case (creates if not exists)
 */
export async function getScope(caseId: string): Promise<CaseScope> {
  return apiClient.get<CaseScope>(`/cases/${caseId}/scope`)
}

/**
 * Update scope document (creates new version)
 */
export async function updateScope(caseId: string, data: UpdateScopeRequest): Promise<CaseScope> {
  return apiClient.put<CaseScope>(`/cases/${caseId}/scope`, data)
}

// =============================================================================
// VERSION API
// =============================================================================

/**
 * List scope versions
 */
export async function listVersions(
  caseId: string,
  params?: ListVersionsQuery
): Promise<CaseScopeVersionList> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CaseScopeVersionList>(`/cases/${caseId}/scope/versions${query}`)
}

/**
 * Get specific version
 */
export async function getVersion(caseId: string, version: number): Promise<CaseScopeVersion> {
  return apiClient.get<CaseScopeVersion>(`/cases/${caseId}/scope/versions/${version.toString()}`)
}

/**
 * Restore to specific version (creates new version with old content)
 */
export async function restoreVersion(caseId: string, version: number): Promise<CaseScope> {
  return apiClient.post<CaseScope>(
    `/cases/${caseId}/scope/versions/${version.toString()}/restore`,
    {}
  )
}

// =============================================================================
// NOTES API
// =============================================================================

/**
 * List scope notes
 */
export async function listNotes(
  caseId: string,
  params?: ListNotesQuery
): Promise<CaseScopeNoteList> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CaseScopeNoteList>(`/cases/${caseId}/scope/notes${query}`)
}

/**
 * Create a note
 */
export async function createNote(
  caseId: string,
  data: CreateScopeNoteRequest
): Promise<CaseScopeNote> {
  return apiClient.post<CaseScopeNote>(`/cases/${caseId}/scope/notes`, data)
}

/**
 * Update a note
 */
export async function updateNote(
  caseId: string,
  noteId: string,
  data: UpdateScopeNoteRequest
): Promise<CaseScopeNote> {
  return apiClient.put<CaseScopeNote>(`/cases/${caseId}/scope/notes/${noteId}`, data)
}

/**
 * Delete a note
 */
export async function deleteNote(caseId: string, noteId: string): Promise<{ success: boolean }> {
  return apiClient.delete<{ success: boolean }>(`/cases/${caseId}/scope/notes/${noteId}`)
}
